<template>
  <div class="h-full grid grid-rows-[auto_1fr]" id="group-items">
    <div class="px-4 py-2 bg-white border-b-4 border-gray-200" id="groups-heading">
      <p class="text-base text-gray-600">Items</p>
    </div>

    <TreeList
      :mode="mode"
      v-model="groupItemNodes"
      has-add-option
      has-rename-option
      has-delete-option
      has-search-input
      is-main-tree
      :selected-node-id="selectedGroupItem?.id"
      @delete-node="onDeleteGroupItem"
      @rename-node="onRenameGroupItem"
      @click-node="onSelectGroupItem" />
  </div>
</template>
<script setup lang="ts">
import { reactive, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useGroupingStore } from '@/stores/grouping';

import TreeList from '../common/TreeList.vue';
import type { TreeType } from '@/types/ComponentTypes';
import { RecordMode } from '@/types/Base';
import type { Group, GroupItem } from '@/types/Groups';
import { deepCopy } from '@/utils/data';

const {
  mode,
  stateGroupItemsNodes,
  groupItemNodes,
  groupItems,
  modifiedGroups,
  groups,
  selectedGroup,
  selectedGroupItem,
} = storeToRefs(useGroupingStore());

const { getGroupItemsTableData } = useGroupingStore();

const selectedGroupNoDisplay = ref<Group>(deepCopy<Group>(selectedGroup.value));
const groupItemsNoDisplay = ref<GroupItem[]>([]);

async function onSelectGroupItem(node: TreeType) {
  selectedGroupItem.value = groupItems.value.find((x) => x.id == node.uid);
  if (!selectedGroupItem.value) return;

  await getGroupItemsTableData({
    page: 1,
    itemsPerPage: 10,
  });
}

function isChildOfNode(parentNode: TreeType, childId: string): boolean {
  if (!parentNode.children || parentNode.children.length === 0) return false;

  for (const child of parentNode.children) {
    if (child.uid === childId) return true;
    if (isChildOfNode(child, childId)) return true;
  }

  return false;
}

function onDeleteGroupItem(node: TreeType) {
  if (
    selectedGroupItem.value &&
    (selectedGroupItem.value.id === node.uid || isChildOfNode(node, selectedGroupItem.value.id))
  ) {
    selectedGroupItem.value = undefined;
  }
  deleteNodeAndChildren(node);
  removeNodeFromTree(node);
}

function deleteNodeAndChildren(node: TreeType) {
  const groupItem = groupItems.value.find((item) => item.id === node.uid);
  if (!groupItem) return;
  groupItem.recordMode = RecordMode.Deleted;

  const group = groups.value.find((x) => x.id === groupItem.groupId);
  if (!group) return;
  group.recordMode = RecordMode.Modified;

  if (!group.groupItems) group.groupItems = reactive([]);

  const existingGroupItem = group.groupItems.find((item) => item.id === groupItem.id);
  if (!existingGroupItem) group.groupItems.push(groupItem);
  else Object.assign(existingGroupItem, groupItem);

  const existingGroup = modifiedGroups.value.find((x) => x.id === group.id);
  if (!existingGroup) modifiedGroups.value.push(group);
  else Object.assign(existingGroup, group);

  if (node.children && node.children.length > 0) {
    node.children.forEach((childNode) => {
      deleteNodeAndChildren(childNode);
    });
  }

  groupItemNodes.value = [...groupItemNodes.value];
}

function removeNodeFromTree(nodeToRemove: TreeType) {
  function findAndRemoveNode(nodes: TreeType[], targetUid: string): boolean {
    const index = nodes.findIndex((node) => node.uid === targetUid);

    if (index !== -1) {
      nodes.splice(index, 1);
      return true;
    }

    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        if (findAndRemoveNode(node.children, targetUid)) {
          return true;
        }
      }
    }

    return false;
  }

  const updatedNodes = [...groupItemNodes.value];
  findAndRemoveNode(updatedNodes, nodeToRemove.uid);

  groupItemNodes.value = updatedNodes;
}

const updateGroupItemNode = ref<TreeType>({
  label: 'New Node',
  expanded: true,
  children: [],
  uid: '',
  sequence: 1,
  level: 1,
  rootCode: '1',
});

function findAndUpdateGroupItemNode(nodes: TreeType[], updatedNode: TreeType) {
  for (const node of nodes) {
    if (node.uid === updatedNode.uid) {
      node.label = updatedNode.label;
      return true;
    } else if (node.children && node.children.length > 0) {
      if (findAndUpdateGroupItemNode(node.children, updatedNode)) {
        return true;
      }
    }
  }
  return false;
}

function onRenameGroupItem(node: TreeType) {
  updateGroupItemNode.value = { ...node };

  findAndUpdateGroupItemNode(groupItemNodes.value, updateGroupItemNode.value);
  groupItemNodes.value = [...groupItemNodes.value];
}

function updateGroupItemsFromNodes(nodes: TreeType[]) {
  function traverse(nodeList: TreeType[], parentId: string | null = null, level = 1) {
    nodeList.forEach((node) => {
      const groupItem = groupItemsNoDisplay.value.find((item) => item.id === node.uid);
      if (groupItem) {
        groupItem.parentGroupItemId = node.parentId ?? null;
        groupItem.rootCode = node.rootCode;
        groupItem.level = level;
        groupItem.sequence = node.sequence;
        groupItem.name = node.label;
        if (groupItem.recordMode != RecordMode.Added) groupItem.recordMode = RecordMode.Modified;
        if (selectedGroupNoDisplay.value && !selectedGroupNoDisplay.value.groupItems) {
          selectedGroupNoDisplay.value.groupItems = [];
        }
        if (!selectedGroupNoDisplay.value.groupItems) return;
        const existingGroupItem = selectedGroupNoDisplay.value.groupItems.find(
          (item) => item.id === groupItem.id
        );
        if (!existingGroupItem) {
          selectedGroupNoDisplay.value.groupItems.push(groupItem);
        } else {
          Object.assign(existingGroupItem, groupItem);
        }
      } else {
        const newGroupItem = {
          id: node.uid,
          groupId: selectedGroupNoDisplay.value.id,
          name: node.label,
          parentGroupItemId: parentId ?? null,
          rootCode: node.rootCode,
          level: level,
          sequence: node.sequence,
          isActive: true,
          mwAccountGroupId: selectedGroupNoDisplay.value.mwAccountGroupId,
          recordMode: RecordMode.Added,
          isFromManager: true,
        };
        groupItemsNoDisplay.value.push(newGroupItem);
      }

      if (node.children && node.children.length > 0) {
        traverse(node.children, node.uid, level + 1);
      }
    });
  }

  selectedGroupNoDisplay.value = deepCopy<Group>(selectedGroup.value);
  groupItemsNoDisplay.value = deepCopy<GroupItem[]>(groupItems.value);
  traverse(nodes);
  groupItems.value = deepCopy<GroupItem[]>(groupItemsNoDisplay.value);
  selectedGroup.value = deepCopy<Group>(selectedGroup.value);
}

function getChangedNodesRecursively(nodeList: TreeType[], oldGroupItems: GroupItem[]): TreeType[] {
  const changedNodes: TreeType[] = [];

  for (const node of nodeList) {
    const oldGroupItem = oldGroupItems.find((gi) => gi.id === node.uid);
    if (!oldGroupItem) {
      changedNodes.push(node);
      continue;
    }
    let changedChildren: TreeType[] = [];
    if (node.children && node.children.length > 0) {
      changedChildren = getChangedNodesRecursively(node.children, oldGroupItems);
    }

    const nodeHasChanges =
      oldGroupItem.name !== node.label ||
      changedChildren.length > 0 ||
      oldGroupItem.parentGroupItemId !== node.parentId ||
      oldGroupItem.rootCode !== node.rootCode ||
      oldGroupItem.sequence !== node.sequence;

    if (nodeHasChanges) {
      changedNodes.push({
        ...node,
        children: changedChildren,
      });
    }
  }

  return changedNodes;
}

watch(
  () => groupItemNodes.value,
  (newData) => {
    const isSame = JSON.stringify(newData) === JSON.stringify(stateGroupItemsNodes.value);
    if (!isSame) {
      if (selectedGroup.value.recordMode === RecordMode.Added && groupItemNodes.value.length === 0)
        return;

      // Get only updated groupItems
      const changedNodes = getChangedNodesRecursively(newData, groupItems.value);
      if (!changedNodes || changedNodes.length == 0) return;
      updateGroupItemsFromNodes(changedNodes);
      if (selectedGroup.value) {
        if (selectedGroup.value.recordMode !== RecordMode.Added) {
          selectedGroup.value.recordMode = RecordMode.Modified;
        }

        selectedGroup.value.groupItems = groupItems.value;

        // Filter only changed group items in the modifiedGroup
        const filterModes = [RecordMode.Added, RecordMode.Modified, RecordMode.Deleted];
        const filteredGroupItems = selectedGroup.value.groupItems.filter(
          (gi) => gi.recordMode && filterModes.includes(gi.recordMode)
        );

        const filteredSelectedGroup = {
          ...selectedGroup.value,
          groupItems: filteredGroupItems,
        };

        const existingIndex = modifiedGroups.value.findIndex(
          (grp) => grp.id === filteredSelectedGroup.id
        );
        if (existingIndex === -1) {
          modifiedGroups.value.push(filteredSelectedGroup);
        } else {
          modifiedGroups.value[existingIndex] = filteredSelectedGroup;
        }
      }
    }
  },
  { deep: true }
);
</script>
